// PURPOSE: Show what was actually drafted, sent and called for one outreach lead, and why it was archived.
// CONTRACT: Spec 088; sent dates are the reported sent_on, never inferred from drafts or due dates. Spec 091: reply sentiment and next step.
// DEPENDS: Native client data (cadence_emails, last_* fields, call_outcome, removed_reason).
// CEILING: Presentation only; no sends, no cadence arithmetic beyond the stored due date.
import { awaitingSend } from './outreach-presentation';
import type { NativeRecord } from './types';

type Copy = { subject?: string; body?: string; drafted_on?: string; sent_on?: string };
const RUNGS: [string, number][] = [['first_touch', 1], ['mark1', 2], ['mark2', 3]];
const CALLS: Record<string, string> = { interested: 'interested', not_interested: 'not interested', no_answer: 'no answer' };
export const ARCHIVE_REASONS: Record<string, string> = {
  hard_no: 'Declined', no_reply: 'No reply after 3 emails', not_fit: 'Not a fit', role_filled: 'Role filled',
  wrong_contact: 'Wrong contact', bounced: 'Email bounced', booked: 'Meeting booked', interested_manual: 'Interested',
};

export function day(value?: unknown) {
  const text = String(value || '');
  const date = new Date(`${text.slice(0, 10)}T12:00:00Z`);
  return text && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Toronto' }).format(date).replace(',', '') : '';
}

function copyOf(record: NativeRecord, rung: string): Copy {
  const all = record.data.cadence_emails;
  const copy = all && typeof all === 'object' && !Array.isArray(all) ? (all as Record<string, Copy>)[rung] : undefined;
  return copy && typeof copy === 'object' ? copy : {};
}

/** One line per email: drafted and sent dates, or when it is due. */
export function emailLine(record: NativeRecord, rung: string, number: number) {
  const copy = copyOf(record, rung);
  const drafted = copy.drafted_on || (record.data.last_drafted_rung === rung ? record.data.last_drafted_on : '');
  const sent = copy.sent_on || (record.data.last_sent_rung === rung ? record.data.last_sent_on : '');
  if (sent) return `Email ${number} · ${drafted ? `drafted ${day(drafted)} · ` : ''}sent ${day(sent)}`;
  if (drafted) return `Email ${number} · drafted ${day(drafted)} · not sent yet`;
  if (record.data.next_touch_rung === rung && record.data.next_touch_due && !awaitingSend(record)) return `Email ${number} · due ${day(record.data.next_touch_due)}`;
  if (record.data.next_touch_rung === rung && record.data.draft_requested_at) return `Email ${number} · draft requested`;
  return `Email ${number} · not drafted`;
}

export function sentCount(record: NativeRecord) {
  return RUNGS.filter(([rung]) => copyOf(record, rung).sent_on || record.data.last_sent_rung === rung).length;
}

export function callSummary(record: NativeRecord) {
  const outcome = CALLS[String(record.data.call_outcome || '')];
  if (outcome) return `Called · last: ${outcome}`;
  return record.data.called_done ? 'Call marked done' : 'Not called yet';
}

const SENTIMENTS: Record<string, string> = { positive: 'positive', neutral: 'neutral', parked: 'parked', negative: 'negative' };

/** Spec 091: how the newest reply read, and the agreed next step. */
export function replySummary(record: NativeRecord) {
  const sentiment = SENTIMENTS[String(record.data.sentiment || '')];
  if (!sentiment) return '';
  const next = String(record.data.next_step || '').trim();
  return `Reply · ${sentiment}${next ? ` · next: ${next}` : ''}`;
}

export function ArchiveReason({ record }: { record: NativeRecord }) {
  if (record.data.outreach_state !== 'archived') return null;
  const reason = ARCHIVE_REASONS[String(record.data.removed_reason || '')] || 'No reason recorded';
  return <p className="outreach-archive-reason"><strong>Archived:</strong> {reason}</p>;
}

export function LeadEmails({ record }: { record: NativeRecord }) {
  const touched = RUNGS.some(([rung]) => copyOf(record, rung).drafted_on || copyOf(record, rung).sent_on) || !!record.data.last_drafted_rung || !!record.data.draft_requested_at;
  return <section aria-label="Emails and calls" className="outreach-emails">
    <h3>Emails · {sentCount(record)} of 3 sent</h3>
    {touched ? <ul>{RUNGS.map(([rung, number]) => {
      const copy = copyOf(record, rung);
      const line = emailLine(record, rung, number);
      return <li key={rung} className={line.includes('not sent yet') ? 'warning' : undefined}>{copy.body ? <details><summary>{line}</summary>{copy.subject && <strong>{copy.subject}</strong>}<p style={{ whiteSpace: 'pre-wrap' }}>{copy.body}</p></details> : line}</li>;
    })}</ul> : <p className="small muted">No email drafted yet.</p>}
    <p className="small">{callSummary(record)}</p>
    {replySummary(record) && <p className="small">{replySummary(record)}</p>}
  </section>;
}
